// §415(b) DB benefit-limit engine for cash-balance modeling, verified against
// a professionally produced 2026 proposal's per-participant
// maximum-contribution schedule used by the combined scenario solver:
//
//   Dollar limit    phased in over 10 years of PARTICIPATION; stated at 62.
//                   Below 62 it is reduced by pure 5% interest discount times
//                   the APR ratio on BOTH the plan basis and the 5% applicable
//                   mortality basis, taking the smaller result (verified:
//                   discount factors like 0.48102 = 1.05^-15 with no
//                   pre-retirement mortality). 62-65 flat. Above 65 it is
//                   actuarially increased at 5% times the plan APR ratio —
//                   labeled `estimated` because the reference schedule embeds a
//                   small (~0.1%/yr) additional mortality adjustment we do not
//                   reproduce.
//   Comp limit      100% of high-3 compensation, phased in over 10 years of
//                   SERVICE (no age adjustment). When explicit high-3 is not
//                   supplied, current compensation is the disclosed proxy.
//   Max credit      the maximum first-year cash-balance pay credit converts
//                   the monthly limit at attained age using the LESSER of the
//                   plan APR and the 5.5% APR.
//   Level credit    the level annual pay credit that accumulates (at the plan
//                   interest credit, end-of-year) to the maximum lump sum at
//                   the participant's retirement age.
//
// Year-specific §415(b) dollar limits live with the other effective-dated
// statutory limits in src/lib/constants.js (PLAN_LIMITS[year].planLimit415b).
// Every result reports the factors used with provenance. Scenario-grade
// modeling, not actuarial certification.

import {
  actuarialBasisDescription,
  annuityFactor,
  DEFAULT_ACTUARIAL_SET,
} from './actuarialAssumptions.js'
import { PLAN_LIMITS } from '../constants.js'

const DOLLAR_LIMIT_STATED_AGE = 62
const LATE_INCREASE_FROM_AGE = 65

export function dollarLimit415bForYear(planYear) {
  return PLAN_LIMITS[Number(planYear)]?.planLimit415b ?? null
}

// Phase-in fraction over 10 years (§415(b)(5)); minimum one year.
export const phasedTenthsFraction = (years, { floorOne = true } = {}) => {
  const clamped = Math.min(10, Math.max(floorOne ? 1 : 0, Math.round(Number(years) || 0)))
  return clamped / 10
}

export function monthlyDollarLimitAtAge({
  age,
  participationYears = 1,
  dollarLimit415b,
  assumptionSet = DEFAULT_ACTUARIAL_SET,
  factors = [],
} = {}) {
  const attained = Math.round(Number(age))
  const annual = Number(dollarLimit415b)
  if (!Number.isFinite(attained) || !(annual > 0)) return null
  const phasedMonthly = (annual * phasedTenthsFraction(participationYears)) / 12

  if (attained >= DOLLAR_LIMIT_STATED_AGE && attained <= LATE_INCREASE_FROM_AGE) {
    return { value: phasedMonthly, adjustment: 'none' }
  }

  if (attained < DOLLAR_LIMIT_STATED_AGE) {
    const years = DOLLAR_LIMIT_STATED_AGE - attained
    const discount = 1.05 ** -years
    const candidates = []
    for (const basis of ['plan', 'applicableMortality5']) {
      const at62 = annuityFactor({ basis, age: DOLLAR_LIMIT_STATED_AGE, assumptionSet })
      const atAge = annuityFactor({ basis, age: attained, assumptionSet })
      if (!at62 || !atAge) return null
      factors.push({ basis, form: 'life', age: attained, ...atAge })
      candidates.push(phasedMonthly * discount * (at62.value / atAge.value))
    }
    return { value: Math.min(...candidates), adjustment: 'reduced-pre-62' }
  }

  // Late-retirement actuarial increase past 65 (estimated; see header).
  const at65 = annuityFactor({ basis: 'plan', age: LATE_INCREASE_FROM_AGE, assumptionSet })
  const atAge = annuityFactor({ basis: 'plan', age: attained, assumptionSet })
  if (!at65 || !atAge) return null
  factors.push({ basis: 'plan', form: 'life', age: attained, value: atAge.value, provenance: 'estimated-late-retirement-increase' })
  const increase = (1.05 ** (attained - LATE_INCREASE_FROM_AGE)) * (at65.value / atAge.value)
  return { value: phasedMonthly * increase, adjustment: 'increased-post-65' }
}

// 100% of high-3 compensation, phased over service. `high3` falls back to
// current compensation as a DISCLOSED proxy (high3Source tells which).
export function monthlyCompLimit({ comp, high3 = null, serviceYears = 0 } = {}) {
  const base = Number(high3) > 0 ? Number(high3) : Number(comp)
  if (!(base > 0)) return null
  return {
    // IRC §415(b)(5)(C) never permits the compensation limb to phase below
    // one tenth, including a newly-entered participant with no prior service.
    value: (base / 12) * phasedTenthsFraction(serviceYears),
    high3Source: Number(high3) > 0 ? 'explicit' : 'current-compensation-proxy',
  }
}

// Maximum first-year cash-balance pay credit for one participant.
export function maxAnnualCashBalanceCredit({
  age,
  comp,
  high3 = null,
  participationYears = 1,
  serviceYears = 0,
  dollarLimit415b,
  assumptionSet = DEFAULT_ACTUARIAL_SET,
} = {}) {
  const factors = []
  const dollar = monthlyDollarLimitAtAge({ age, participationYears, dollarLimit415b, assumptionSet, factors })
  const compLimit = monthlyCompLimit({ comp, high3, serviceYears })
  if (!dollar || compLimit === null) {
    return { status: 'unavailable', reason: !dollar ? 'missing-apr-for-age' : 'invalid-compensation' }
  }
  const monthlyLimit = Math.min(dollar.value, compLimit.value)
  const planApr = annuityFactor({ basis: 'plan', age, assumptionSet })
  const lumpApr = annuityFactor({ basis: 'lumpSum55', age, assumptionSet })
  if (!planApr || !lumpApr) return { status: 'unavailable', reason: 'missing-apr-for-age' }
  factors.push(
    { basis: 'plan', form: 'life', age: Math.round(Number(age)), ...planApr },
    { basis: 'lumpSum55', form: 'life', age: Math.round(Number(age)), ...lumpApr },
  )
  const conversionApr = Math.min(planApr.value, lumpApr.value)
  return {
    status: 'ok',
    monthlyLimit,
    limitBasis: compLimit.value <= dollar.value ? 'compensation' : 'dollar',
    high3Source: compLimit.high3Source,
    dollarAdjustment: dollar.adjustment,
    conversionApr,
    conversionBasis: planApr.value <= lumpApr.value ? 'plan' : 'lumpSum55',
    maxCredit: monthlyLimit * conversionApr,
    factors,
    assumptions: limits415bAssumptions(assumptionSet),
  }
}

// §415(b) maximum funding: level annual credit to the maximum lump sum at
// retirement age (end-of-year credits at the plan interest credit).
//
// The lump-sum conversion uses the LESSER of the plan APR and the 5.5% APR,
// the same §415(b)(2)(E) rule maxAnnualCashBalanceCredit applies: the statute
// fixes the conversion interest rate at the GREATER of the plan rate or 5.5%,
// and a greater rate produces a smaller annuity factor. Using the plan APR
// alone overstates the permitted lump sum wherever the plan basis is the more
// generous one, which is every retirement age below roughly 59 in the anchored
// tables. At the usual 62-65 the plan APR is already the lesser, so this does
// not move a typical design.
//
// When the 5.5% table has no anchor at the retirement age the comparison
// cannot be made; rather than refuse to solve, the plan APR is used alone and
// `conversionBasis` reports 'plan-no-55-anchor' so the caller can disclose
// that the §415(b)(2)(E) floor was not verified at that age.
export function solveMaxCashBalanceCredit({
  comp,
  high3 = null,
  retirementAge,
  fundingYears,
  serviceYears = 0,
  interestCredit = 0.05,
  dollarLimit415b,
  planAprTable,
  assumptionSet = DEFAULT_ACTUARIAL_SET,
} = {}) {
  const aprFactor = annuityFactor({ basis: 'plan', age: retirementAge, customTable: planAprTable, assumptionSet })
  const compensation = Number(comp) || 0
  const years = Math.max(1, Math.round(Number(fundingYears) || 0))
  const creditRate = Number(interestCredit)
  if (!aprFactor || compensation <= 0 || !(Number(dollarLimit415b) > 0)
    || !Number.isFinite(creditRate) || creditRate < 0) {
    return { status: 'unavailable', reason: !aprFactor ? 'missing-apr-for-age' : 'invalid-inputs' }
  }
  const dollar = monthlyDollarLimitAtAge({
    age: retirementAge,
    participationYears: years,
    dollarLimit415b,
    assumptionSet,
  })
  const compMonthly = monthlyCompLimit({
    comp: compensation,
    high3,
    serviceYears: Math.max(0, Number(serviceYears) || 0) + years,
  })
  if (!dollar || compMonthly === null) return { status: 'unavailable', reason: 'missing-apr-for-age' }
  const monthlyLimit = Math.min(compMonthly.value, dollar.value)
  const lumpAprFactor = annuityFactor({ basis: 'lumpSum55', age: retirementAge, assumptionSet })
  const conversionApr = lumpAprFactor
    ? Math.min(aprFactor.value, lumpAprFactor.value)
    : aprFactor.value
  const conversionBasis = !lumpAprFactor
    ? 'plan-no-55-anchor'
    : aprFactor.value <= lumpAprFactor.value ? 'plan' : 'lumpSum55'
  const maxLumpSum = monthlyLimit * conversionApr
  // End-of-year credits: sum of (1+i)^k for k = 0..years-1.
  const accumulation = creditRate === 0
    ? years
    : (((1 + creditRate) ** years) - 1) / creditRate
  const levelCredit = maxLumpSum / accumulation
  return {
    status: 'solved',
    monthlyLimit,
    limitBasis: compMonthly.value <= dollar.value ? 'compensation' : 'dollar',
    high3Source: compMonthly.high3Source,
    dollarAdjustment: dollar.adjustment,
    maxLumpSum,
    levelCredit,
    conversionApr,
    conversionBasis,
    suggestedCredit: Math.ceil(levelCredit / 1000) * 1000,
    assumptions: [
      ...limits415bAssumptions(assumptionSet),
      ...(conversionBasis === 'plan-no-55-anchor'
        ? [`The ${actuarialBasisDescription('lumpSum55', assumptionSet)} table has no anchor at this retirement age, so the maximum lump sum was converted on the plan basis alone and the §415(b)(2)(E) floor was not verified there.`]
        : []),
    ],
  }
}

export function limits415bAssumptions(assumptionSet = DEFAULT_ACTUARIAL_SET) {
  return [
    `Dollar limit reduced before 62 by 5% interest only (no pre-retirement mortality) on the lesser of ${actuarialBasisDescription('plan', assumptionSet)} and ${actuarialBasisDescription('applicableMortality5', assumptionSet)}.`,
    'Dollar limit increased past 65 at 5% times the plan APR ratio (estimated; reference schedules embed a further ~0.1%/yr mortality adjustment).',
    'Compensation limit is 100% of high-3 pay (current compensation as a disclosed proxy when high-3 is not supplied), phased in over 10 years of service; the dollar limit phases in over 10 years of participation.',
    `Maximum credit and the maximum lump sum both convert the monthly limit with the LESSER of ${actuarialBasisDescription('plan', assumptionSet)} and ${actuarialBasisDescription('lumpSum55', assumptionSet)} (§415(b)(2)(E) fixes the rate at the greater of the plan rate or 5.5%, which is the smaller annuity factor).`,
  ]
}
